import {
  notEmpty,
  theme,
  useResponsive,
  useTrackImpressionEventByIntersection,
} from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import take from "lodash/take";
import type { FunctionComponent, ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { Pagination } from "swiper/modules";

import { articleSponsorType } from "shared/data/article";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import {
  ExperimentAPINames,
  useActiveExperiment,
  useOptimizelyContext,
} from "scmp-app/lib/optimizely";
import { rosettaAtom } from "scmp-app/lib/rosetta/atoms";
import type { articleFurtherReadingWidgetArticle$key } from "scmp-app/queries/__generated__/articleFurtherReadingWidgetArticle.graphql";

import { useFurtherReadingWidgetTopPicks } from "./hooks";
import {
  AdSlot,
  Container,
  FurtherReadingContainer,
  StoriesYouMightLikeContainer,
  StyledContentItemArticleWidget,
  StyledSwiper,
  StyledSwiperSlide,
  Title,
} from "./styles";

type Props = {
  className?: string;
  inlineAds?: ReactNode;
  reference: articleFurtherReadingWidgetArticle$key;
  variant?: "inline" | "right-hand-side";
};

export const ArticleFurtherReadingWidget: FunctionComponent<Props> = ({
  className,
  inlineAds,
  reference,
  variant,
}) => {
  const article = useFragment(
    graphql`
      fragment articleFurtherReadingWidgetArticle on Article {
        entityId
        sponsorType
        moreOnThisArticles {
          ...articleWidgetContent
        }
      }
    `,
    reference,
  );

  const { fireEvent } = useOptimizelyContext();
  const { experiment } = useActiveExperiment(ExperimentAPINames.ArticleRecirculationWidgets);
  const isTextDrivenVariation = useMemo(
    () => experiment?.variation.name === "1",
    [experiment?.variation.name],
  );

  const isDesktop = useResponsive(theme.breakpoints.up("desktop"));
  const isMobile = useResponsive(theme.breakpoints.only("mobile"));
  const asyncRosettaState = useAtomValue(rosettaAtom);

  const isMatchVariant = useMemo(
    () => (isDesktop && variant === "right-hand-side") || (!isDesktop && variant === "inline"),
    [isDesktop, variant],
  );

  const isSubscriber = useMemo(
    () => asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false,
    [asyncRosettaState?.result?.user?.isSCMPSubscriber],
  );

  const moreOnThisArticles = useMemo(
    () => take(article.moreOnThisArticles?.filter(notEmpty) ?? [], isSubscriber ? 4 : 3),
    [article.moreOnThisArticles, isSubscriber],
  );

  const { aiRecommendationId, topPicks } = useFurtherReadingWidgetTopPicks(
    article.entityId,
    isMatchVariant && !isSubscriber,
  );

  const isShowFurtherReading = useMemo(() => notEmpty(moreOnThisArticles), [moreOnThisArticles]);

  const [firstPartTopPicks, secondPartTopPicks] = useMemo(
    () =>
      isShowFurtherReading
        ? [topPicks.slice(0, 1), topPicks.slice(1)]
        : [topPicks.slice(0, 2), topPicks.slice(2)],
    [isShowFurtherReading, topPicks],
  );

  const isShowStoriesYouMightLike = useMemo(
    () => !isSubscriber && !isTextDrivenVariation && firstPartTopPicks.length > 0,
    [firstPartTopPicks.length, isSubscriber, isTextDrivenVariation],
  );

  const shouldShowSwiper = useMemo(() => isMobile && !isSubscriber, [isMobile, isSubscriber]);

  const readMoreGA4ImpressionEvent: () => RecirculationWidgetImpressionEvent = () =>
    ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "article",
        widget_name: "read_more",
        widget_version: isSubscriber ? "subscriber" : "non_subscriber",
        ...(shouldShowSwiper ? { widget_tab: 1 } : {}),
      },
      subcategory: "widget",
    }) as const;

  const {
    captureTrackImpressionEventTargetElement: readMoreCaptureTrackImpressionEventTargetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: readMoreGA4ImpressionEvent,
    options: {
      intersectionThreshold: [0.1],
      isSendGA4Tracking: true,
      shouldSendOnce: true,
      shouldThrottle: true,
    },
  });

  const firstPartTopPicksGA4ImpressionEvent: () => RecirculationWidgetImpressionEvent = () =>
    ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: topPicks.map(article => article.entityId).join(","),
        page_type: "article",
        recommendation_id: aiRecommendationId,
        widget_name: "top_picks",
        widget_version: isSubscriber ? "subscriber" : "non_subscriber",
        ...(shouldShowSwiper ? { widget_tab: 1 } : {}),
      },
      subcategory: "widget",
    }) as const;

  const {
    captureTrackImpressionEventTargetElement:
      firstPartTopPicksCaptureTrackImpressionEventTargetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: firstPartTopPicksGA4ImpressionEvent,
    options: {
      intersectionThreshold: [0.1],
      isSendGA4Tracking: true,
      shouldSendOnce: true,
      shouldThrottle: true,
    },
  });

  const getFurtherReadingContentItemArticleWidgetClickHandler = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        page_type: "article",
        widget_name: "read_more",
        widget_version: isSubscriber ? "subscriber" : "non_subscriber",
        ...(shouldShowSwiper ? { widget_tab: 1 } : {}),
      },
      subcategory: "widget",
    });
    fireEvent?.({
      eventName: "item_click",
    });
  }, [fireEvent, isSubscriber, shouldShowSwiper]);

  const getFirstPartTopPicksContentItemArticleWidgetClickHandler = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: topPicks.map(article => article.entityId).join(","),
        page_type: "article",
        recommendation_id: aiRecommendationId,
        widget_name: "top_picks",
        widget_version: isSubscriber ? "subscriber" : "non_subscriber",
        ...(shouldShowSwiper ? { widget_tab: 1 } : {}),
      },
      subcategory: "widget",
    });
    fireEvent?.({
      eventName: "item_click",
    });
  }, [aiRecommendationId, fireEvent, isSubscriber, shouldShowSwiper, topPicks]);

  const renderFurtherReading = useCallback(
    () => (
      <FurtherReadingContainer>
        {isShowFurtherReading && (
          <>
            <div ref={readMoreCaptureTrackImpressionEventTargetElement} />
            <Title>Further Reading</Title>
            {moreOnThisArticles.map((node, index) => (
              <StyledContentItemArticleWidget
                key={index}
                onClick={getFurtherReadingContentItemArticleWidgetClickHandler}
                query={{
                  module: "further_reading_RM",
                  pgtype: "article",
                }}
                reference={node}
                withCoverImage={!isSubscriber && !isTextDrivenVariation}
              />
            ))}
          </>
        )}

        {isShowStoriesYouMightLike && <Title>Stories You Might Like</Title>}

        {inlineAds && (isShowFurtherReading || isShowStoriesYouMightLike) && (
          <AdSlot>{inlineAds}</AdSlot>
        )}

        {isShowStoriesYouMightLike && (
          <>
            <div ref={firstPartTopPicksCaptureTrackImpressionEventTargetElement} />
            {firstPartTopPicks.map((node, index) => (
              <StyledContentItemArticleWidget
                key={index}
                onClick={getFirstPartTopPicksContentItemArticleWidgetClickHandler}
                query={{
                  module: "further_reading_AI",
                  pgtype: "article",
                }}
                reference={node}
                withCoverImage={!isTextDrivenVariation}
              />
            ))}
          </>
        )}
      </FurtherReadingContainer>
    ),
    [
      firstPartTopPicks,
      firstPartTopPicksCaptureTrackImpressionEventTargetElement,
      getFirstPartTopPicksContentItemArticleWidgetClickHandler,
      getFurtherReadingContentItemArticleWidgetClickHandler,
      inlineAds,
      isShowFurtherReading,
      isShowStoriesYouMightLike,
      isSubscriber,
      isTextDrivenVariation,
      moreOnThisArticles,
      readMoreCaptureTrackImpressionEventTargetElement,
    ],
  );

  const secondPartTopPicksGA4ImpressionEvent: () => RecirculationWidgetImpressionEvent = () =>
    ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: topPicks.map(article => article.entityId).join(","),
        page_type: "article",
        recommendation_id: aiRecommendationId,
        widget_name: "top_picks",
        widget_tab: 2,
        widget_version: isSubscriber ? "subscriber" : "non_subscriber",
      },
      subcategory: "widget",
    }) as const;

  const {
    captureTrackImpressionEventTargetElement:
      secondPartTopPicksCaptureTrackImpressionEventTargetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: secondPartTopPicksGA4ImpressionEvent,
    options: {
      intersectionThreshold: [0.1],
      isSendGA4Tracking: true,
      shouldSendOnce: true,
      shouldThrottle: true,
    },
  });

  const getSecondPartTopPicksContentItemArticleWidgetClickHandler = useCallback(() => {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: topPicks.map(article => article.entityId).join(","),
        page_type: "article",
        recommendation_id: aiRecommendationId,
        widget_name: "top_picks",
        widget_version: isSubscriber ? "subscriber" : "non_subscriber",
        ...(shouldShowSwiper ? { widget_tab: 2 } : {}),
      },
      subcategory: "widget",
    });
    fireEvent?.({
      eventName: "item_click",
    });
  }, [aiRecommendationId, fireEvent, isSubscriber, shouldShowSwiper, topPicks]);

  const renderStoriesYouMightLike = useCallback(
    (shouldShowSwiper: boolean) => {
      if (!isShowStoriesYouMightLike || secondPartTopPicks.length === 0) return null;
      return (
        <StoriesYouMightLikeContainer
          ref={
            shouldShowSwiper
              ? secondPartTopPicksCaptureTrackImpressionEventTargetElement
              : undefined
          }
        >
          {secondPartTopPicks.map((node, index) => (
            <StyledContentItemArticleWidget
              key={index}
              onClick={getSecondPartTopPicksContentItemArticleWidgetClickHandler}
              query={{
                module: "further_reading_AI",
                pgtype: "article",
              }}
              reference={node}
              withCoverImage={!isTextDrivenVariation}
            />
          ))}
        </StoriesYouMightLikeContainer>
      );
    },
    [
      getSecondPartTopPicksContentItemArticleWidgetClickHandler,
      isShowStoriesYouMightLike,
      isTextDrivenVariation,
      secondPartTopPicks,
      secondPartTopPicksCaptureTrackImpressionEventTargetElement,
    ],
  );

  const render = useCallback(
    () => (
      <>
        {renderFurtherReading()}
        {renderStoriesYouMightLike(false)}
      </>
    ),
    [renderFurtherReading, renderStoriesYouMightLike],
  );

  const renderSwiper = useCallback(() => {
    const children = [renderFurtherReading(), renderStoriesYouMightLike(true)].filter(notEmpty);
    return (
      <StyledSwiper
        breakpoints={{
          0: {
            spaceBetween: 20,
          },
        }}
        modules={[Pagination]}
        pagination={true}
      >
        {children.map((child, index) => (
          <StyledSwiperSlide key={index}>{child}</StyledSwiperSlide>
        ))}
      </StyledSwiper>
    );
  }, [renderFurtherReading, renderStoriesYouMightLike]);

  const isSponsoredContent = articleSponsorType.has(article.sponsorType ?? "");

  if (isSponsoredContent || !isMatchVariant) return null;

  return (
    (isShowFurtherReading || isShowStoriesYouMightLike) && (
      <Container $isSubscriber={isSubscriber} className={className}>
        {isMobile ? renderSwiper() : render()}
      </Container>
    )
  );
};

ArticleFurtherReadingWidget.displayName = "ArticleFurtherReadingWidget";
